import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, euro } from "@/lib/api";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PackageCheck, Leaf, Truck } from "lucide-react";

const FEATURE_LABELS = {
  loulav: "Loulav", etrog: "Etrog", hadassim: "Hadassim", aravot: "Aravot",
  coffret: "Coffret", provenance: "Provenance", supervision: "Supervision",
};

export default function ProductPage() {
  const { gammeKey } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/products/${gammeKey}`).then(({ data }) => setP(data)).catch(() => {}); }, [gammeKey]);
  if (!p) return <div className="brand-container py-20 text-center text-brand-green">Chargement…</div>;

  const disp = (v) => (!v || v === "à confirmer" ? "n.c." : v);

  return (
    <div className="brand-container py-14">
      <Link to="/" className="text-sm text-brand-green/60 hover:text-brand-green">← Retour</Link>
      <div className="grid lg:grid-cols-2 gap-10 mt-6">
        <div>
          {p.images?.[0] ? (
            <img src={p.images[0]} alt={`Set ${p.name}`} data-testid="product-image" className="rounded-3xl aspect-square w-full object-cover shadow-xl" />
          ) : (
            <>
              <ProductPlaceholder name={p.name} className="rounded-3xl aspect-square shadow-xl" />
              <div className="grid grid-cols-4 gap-2 mt-3">
                {[0, 1, 2, 3].map((i) => (<ProductPlaceholder key={i} name="" className="rounded-lg aspect-square opacity-60" />))}
              </div>
            </>
          )}
        </div>
        <div>
          <span className="rounded-full bg-brand-sage px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-green">{p.position_badge}</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-brand-green mt-3">Set {p.name}</h1>
          <div className="mb-6 mt-4">
            <span className="num text-5xl text-brand-green">{euro(p.price_ttc)}</span>
            <span className="text-sm text-brand-green/50 ml-2">TTC</span>
          </div>
          <ul className="space-y-2 mb-6">
            {p.arguments.map((a, i) => (<li key={`${a}-${i}`} className="flex gap-2 text-brand-green/85"><Leaf className="w-4 h-4 text-brand-gold shrink-0 mt-1" /> {a}</li>))}
          </ul>
          <Link to={`/commander?gamme=${p.gamme_key}`} data-testid="product-reserve" className="inline-block rounded-full px-8 py-4 font-bold text-base bg-brand-gold text-brand-green hover:bg-brand-goldHover">
            Réserver ce set
          </Link>

          <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="font-serif text-xl font-bold text-brand-green mb-3">Contenu du set</h3>
            <ul className="grid grid-cols-2 gap-2 text-sm text-brand-green/80">
              {p.content.map((c, i) => (<li key={`${c}-${i}`} className="flex gap-2"><PackageCheck className="w-4 h-4 text-brand-gold" /> {c}</li>))}
            </ul>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 mt-12">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="font-serif text-2xl font-bold text-brand-green mb-4">Caractéristiques</h3>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(p.features).map(([k, v]) => (
                <tr key={k} className="border-b border-brand-green/10">
                  <td className="py-2.5 text-brand-green/60">{FEATURE_LABELS[k] || k}</td>
                  <td className="py-2.5 text-right font-medium text-brand-green">{disp(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div className="flex flex-wrap gap-4 mb-6">
            {[["Vérifié kasher", PackageCheck], ["Livraison offerte", Truck], ["Rappel personnalisé", Leaf]].map(([t, Icon]) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm text-brand-green/75"><Icon className="w-4 h-4 text-brand-gold" /> {t}</span>
            ))}
          </div>
          <Accordion type="single" collapsible>
            <AccordionItem value="a1"><AccordionTrigger className="text-brand-green font-semibold">Validité hala'hique</AccordionTrigger><AccordionContent className="text-brand-green/70">Set vérifié avant la remise (loulav, etrog, hadassim, aravot).</AccordionContent></AccordionItem>
            <AccordionItem value="a2"><AccordionTrigger className="text-brand-green font-semibold">Livraison &amp; retrait</AccordionTrigger><AccordionContent className="text-brand-green/70">Livraison offerte en Île-de-France ou retrait gratuit dans le 19e. Nous convenons de l'horaire par téléphone, livré avant l'entrée de la fête.</AccordionContent></AccordionItem>
            <AccordionItem value="a3"><AccordionTrigger className="text-brand-green font-semibold">Échange en cas de défaut</AccordionTrigger><AccordionContent className="text-brand-green/70">Indiquez vos réserves à la remise et contactez-nous immédiatement.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
